/**
 * THE SIGN-IN BANNER. Every class it emits already has a CSS rule.
 * ===============================================================
 *
 * `test/css-contract.test.ts` exists because a component once emitted six class names no stylesheet
 * defined, and the consequence was not cosmetic — the affordance that keeps a prefilled value from
 * looking like the technician's own answer rendered as plain text. So this component deliberately
 * introduces **no new class names**: it reuses `banner`, `banner--{tone}`, `banner__title`,
 * `banner__body`, `button`, `fx-quote` and `mono`, exactly as `ApiNotice.tsx` does. No stylesheet
 * needed changing to add this, which is the point.
 *
 * The wording is not here. It is in `phase.ts`'s {@link describeAuthPhase}, which is a pure function in
 * the node test project — so the distinction between "never signed in", "your session expired while you
 * were offline" and "sign-in did not finish" is asserted without a DOM, and this file is the thin part
 * that can only get the markup wrong.
 *
 * **THERE IS NO CREDENTIAL INPUT HERE AND THERE MUST NEVER BE ONE.** Technicians authenticate on the
 * IdPrism portal with a password and a device-bound FIDO2 passkey. The only control this component
 * renders is a button that starts a redirect.
 */

import { describeAuthPhase, type AuthPhase } from './phase';

export interface SignInBannerProps {
  readonly phase: AuthPhase;
  readonly online: boolean;
  /** Starts the redirect. Absent when no sign-in can be started, in which case no button renders. */
  readonly onSignIn?: () => void;
}

export function SignInBanner({ phase, online, onSignIn }: SignInBannerProps): JSX.Element | null {
  const notice = describeAuthPhase(phase, online);

  /*
   * Quiet when signed in, for the reason `StorageBanner` is quiet when storage is durable: a permanent
   * green banner is a banner nobody reads, which is a problem on the day it turns into a warning. The
   * standing state belongs on the Device screen, not above every job.
   */
  if (phase.kind === 'signed-in') {
    return null;
  }

  return (
    <div
      className={`banner banner--${notice.tone}`}
      /*
       * `alert` interrupts a screen reader mid-sentence. Right for the two states where the technician
       * has to act (`session-expired`, `sign-in-failed`), wrong for the ones where the honest message
       * is "carry on, nothing is wrong" — being told assertively that you are offline and fine is how a
       * user learns to talk over the alert that matters. Same rule as `ApiNotice.tsx`.
       */
      role={notice.tone === 'danger' ? 'alert' : 'status'}
      {...(notice.tone === 'danger' ? {} : { 'aria-live': 'polite' as const })}
      data-auth-phase={phase.kind}
    >
      <p className="banner__title">{notice.title}</p>
      <p className="banner__body">{notice.body}</p>
      {notice.canStartSignIn && onSignIn !== undefined ? (
        <p>
          <button type="button" className="button" onClick={onSignIn}>
            Sign in
          </button>
        </p>
      ) : null}
      {notice.detail !== undefined ? (
        <p className="fx-quote">
          <span className="fx-quote__label">Quote this if you report it</span>
          <span className="mono fx-quote__value">{notice.detail}</span>
        </p>
      ) : null}
    </div>
  );
}
